import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AddIcon from '@wix/da-ds/pkg/Icons/24x24/Add';
import { useDispatch, useSelector } from 'react-redux';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { getDeviationEntityId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { createTipJarTier } from '@wix/da-shared-react/pkg/Tier/redux/creator/actions';
import TierCreatorReduxModule from '@wix/da-shared-react/pkg/Tier/redux/creator/ModuleLoadable';
import { useVerification } from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { AppState } from '../../../../types/store';
import useModuleData from '../../../../utils/hooks/useModuleData';
import { setSelectedTierEntityId } from '../../../actions/subscriptionsSection';
import { getSelectedTier } from '../../../selectors/subscriptionsSection';
import WidgetBox, { Props as WidgetBoxProps } from '../_partials/WidgetBox';
import Tiers from '../Tiers';

const ProductBox: React.FC<WidgetBoxProps> = ({
  moduleId,
  widgetMoveUp,
  widgetMoveDown,
  widgetRemove,
  isOwner,
  profileOwnerUser,
  section,
  zone,
  ...props
}) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const {
    moduleData: { tiers = [] },
  } = useModuleData(moduleId);
  const count = tiers.length;
  const dispatch = useDispatch();
  const isEmpty = count === 0;
  const selectedTier = useSelector<AppState, any>(state =>
    getSelectedTier(state)
  );
  const selectTier = useCallback(
    tier => {
      if (tier.deviationId !== selectedTier?.deviationId) {
        logBiEvent({
          evid: BiEventType.TIER_NAVIGATION_SELECT,
          typeid: getDeviationResourceType(tier),
          itemid: tier.deviationId,
          authorid: tier?.author?.useridUuid,
        });
      }
      dispatch(setSelectedTierEntityId(getDeviationEntityId(tier)));
    },
    [logBiEvent, dispatch, selectedTier]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const selectedTierId = urlParams.get('tier');
    if (selectedTierId) {
      dispatch(setSelectedTierEntityId(selectedTierId));
    }
    // run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTipJar = useCallback(() => {
    dispatch(createTipJarTier());
  }, [dispatch]);
  // users who are gifted core can see these buttons even before they're verified
  const { restrictToVerified } = useVerification();
  const restrictedCreateTipJar = restrictToVerified(
    'createsubscription',
    createTipJar
  );

  return (
    <WidgetBox
      section={section}
      zone={zone}
      header={t('widgets.tiers.header', { count })}
      headerExtras={
        isOwner && (
          <>
            <TextButton
              icon={AddIcon}
              size={'medium'}
              variant="main"
              onClick={restrictedCreateTipJar}
              biData={{
                evid: BiEventType.TIER_ADD_NEW,
                is_tip_jar: 1,
                is_empty_state_view: isEmpty ? 1 : 0,
                widgetname: 'tiers_product',
              }}
            >
              {t('widgets.tiers.header.button.createTipJar')}
            </TextButton>
            <TierCreatorReduxModule />
          </>
        )
      }
      count={count > 1 ? count : undefined}
      hasMore={false}
      moduleId={moduleId}
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      isEmpty={isEmpty}
      biData={{
        referral_info: ReferralType.SUBSCRIPTIONS_WIDGET,
        widgetname: 'tiers_product',
      }}
    >
      <Tiers
        moduleId={moduleId}
        view={'product'}
        withExpandButton={true}
        {...props}
        isOwner={isOwner}
        onClick={selectTier}
        onSelect={selectTier}
        selectedTierId={selectedTier && selectedTier.deviationId}
      />
    </WidgetBox>
  );
};

ProductBox.displayName = 'ProductBox';
export default ProductBox;
